import { showError, showSuccess } from '@calendly/alert';
import { useFlag } from '@calendly/feature-check-react';
import { useBooleanState } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { IconButton } from '@calendly/ui/components/icon-button';
import { ArrowRoundLeftIcon, ArrowRoundRightIcon, BlockIcon, CloseCircleIcon, EditPencilIcon, MailSendEnvelopeIcon, SettingsMenuVerticalIcon, UserSingleIcon } from '@calendly/ui/components/icons';
import { BareItem, PopupDropdownMenu } from '@calendly/ui/components/menu';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BlockModal, UnblockModal, BlockExpiredModal } from 'components/meetings/block_modals';
import NoShowModal from 'components/scheduled_events/modals/no_show';
import { HostReschedule } from 'features/host_reschedule';
import { CRM_SHAREABLE } from 'features/mfe_shareable';
import { useRightRail } from 'features/right_rail';
import { SendPopup, useSendEmailsMutation, FlowKind, closeSendPopup } from 'features/send_from_calendly';
import { CONTACT_INTERACTION_LOCATIONS } from 'hooks/analytics_events/constants';
import { useOpenEmailComposerFromHost } from 'hooks/oap_web/use_open_email_composer_from_host';
import { isInviteeOlderThan90Days } from './block_expiry_utils';
import { CancelInviteeModal } from './cancel_invitee_modal';
import { ItemContents } from './item_contents';
import { useNoShow } from './use_no_show';
const primaryInviteeActionButtonCss = "p1kvepni";
const primaryActionsRowCss = "p18zasxk";
const primaryActionsRowThreeItemsCss = "p4tel3d";
const inviteeActionsKebabHostCss = "ie7m3r7";
const PrimaryActionsRow = forwardRef((props, ref) => {
  const {
    as: AsComponent = 'div',
    ...restProps
  } = props;
  const Component = AsComponent;
  return <Component ref={ref} {...restProps} className={classNames(primaryActionsRowCss, props.className)} />;
});
const styledMenuCss = "scb08g0";
const StyledMenu = forwardRef((props, ref) => <PopupDropdownMenu ref={ref} {...props} className={classNames(styledMenuCss, props.className)} />);

/** Passed to HostReschedule as `Button` for kebab reschedule (stable ref for eslint react/no-unstable-nested-components). */
const RescheduleKebabMenuButton = ({
  onClick
}) => <BareItem onClick={onClick}>
    <ItemContents>
      <ArrowRoundRightIcon spacingRight={4} />
      {I18n.t('publisher.meetings.invitees.reschedule_invitee')}
    </ItemContents>
  </BareItem>;
export const InviteeActions = ({
  event,
  invitee,
  canReschedule,
  canCancel,
  canBlock,
  canUnblock,
  openEditEmailModal,
  canEditEmail,
  hideRescheduleFromMenu = false,
  hideRemoveFromMenu = false,
  hideBlockFromMenu = false,
  hideUnblockFromMenu = false,
  showPrimaryActions = false,
  resolved,
  canMarkNoShow,
  onMarkAsNoShow,
  sendAnalytics,
  /** When true, kebab menu is omitted entirely (e.g. external meeting invitees). */
  hideKebabMenu = false
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    close
  } = useRightRail();
  const [sendEmails] = useSendEmailsMutation();
  const isCRMContactCardFlagEnabled = useFlag('crm_contactcard_feature');
  const isCRMContactDetailFlagEnabled = useFlag('crm_contactdetails_feature');
  const isCRMContactDetailsPageEnabled = isCRMContactCardFlagEnabled && isCRMContactDetailFlagEnabled;
  const isNewEmailComposerEnabled = useFlag('crm_new_email_composer');
  const openHostEmailComposer = useOpenEmailComposerFromHost();
  const [toggleGroupMenu, Dropdown, isExpanded] = useDropdown();
  const [isEmailPopupOpen, openEmailPopup, closeEmailPopup] = useBooleanState(false);
  const [isCancelInviteeModalOpen, openCancelInviteeModal, closeCancelInviteeModal] = useBooleanState(false);
  const [isBlockModalOpen, openBlockModal, closeBlockModal] = useBooleanState(false);
  const [isUnblockModalOpen, openUnblockModal, closeUnblockModal] = useBooleanState(false);
  const [isBlockExpiredModalOpen, openBlockExpiredModal, closeBlockExpiredModal] = useBooleanState(false);
  const {
    isModalOpen: isNoShowModalOpen,
    closeModal: closeNoShowModal,
    handleMarkNoShow: handleMarkNoShowConfirm,
    handleUndoNoShow,
    handleNoShowClick
  } = useNoShow({
    invitee,
    event,
    onMarkAsNoShow: onMarkAsNoShow ?? (() => {})
  });
  const isBlocked = invitee.blockedByHost;
  const isOlderThan90Days = isInviteeOlderThan90Days(invitee.createdAt);
  const handleBlockClick = () => {
    if (isOlderThan90Days) {
      openBlockExpiredModal();
    } else {
      openBlockModal();
    }
  };
  const handleViewFullProfile = () => {
    const profileRouteId = resolved?.contactId ?? resolved?.contactUuid;
    if (sendAnalytics && resolved) {
      sendAnalytics('contact_details_view', resolved.contactUuid ?? resolved.contactId);
    }
    close();
    if (profileRouteId) history.push(`/app/contacts/${profileRouteId}`);
  };
  const handleEmailClick = () => {
    dispatch(closeSendPopup());
    const contactRecordId = resolved?.contactId ?? resolved?.contactUuid;
    if (isNewEmailComposerEnabled && contactRecordId) {
      openHostEmailComposer({
        recipientIds: [contactRecordId],
        sendViaCalendlySource: CONTACT_INTERACTION_LOCATIONS.MEETINGS_PAGE_RIGHT_RAIL
      });
    } else {
      openEmailPopup();
    }
  };
  const handleEmailSend = async values => {
    if (!resolved) return;
    const {
      error
    } = await sendEmails({
      sendWith: values.sendWith ? `${values.sendWith.type}_${values.sendWith.accountUuid}` : null,
      to: values.to[0]?.email,
      cc: values.cc.map(({
        email
      }) => email),
      bcc: values.bcc.map(({
        email
      }) => email),
      body: values.body,
      subject: values.subject?.trim() || I18n.t('publisher.send_from_calendly.fields.subject.no_subject'),
      remindAt: values.isReminderActive ? values.remindInDays : undefined,
      inviteeUuid: invitee?.uuid,
      contactUuid: resolved.contactUuid ?? resolved.contactId,
      eventTypeUuid: null,
      kind: FlowKind.contact,
      rescheduleViaShareAvailability: false,
      includeCancellationPolicy: false
    });
    if (error) {
      dispatch(showError(I18n.t('publisher.send_from_calendly.messages.error')));
      return;
    }
    dispatch(showSuccess(I18n.t('publisher.send_from_calendly.messages.success')));
    closeEmailPopup();
  };
  const emailInitialValues = resolved ? {
    sendWith: null,
    to: [{
      id: resolved.contactId ?? '',
      email: resolved.email ?? '',
      name: resolved.name ?? ''
    }],
    cc: [],
    showCc: false,
    bcc: [],
    showBcc: false,
    subject: '',
    body: `<p>${I18n.t('publisher.mailer.greeting_hello', {
      name: resolved.name ?? ''
    })}</p>`,
    timeSlotSuggestions: null,
    remindInDays: 3,
    kind: FlowKind.contact,
    isReminderActive: false
  } : null;
  const hasContactForPrimaryEmail = Boolean(resolved?.contactUuid || resolved?.contactId);
  const primaryActionButtonCount = (canMarkNoShow && !invitee.blockedByHost ? 1 : 0) + (hasContactForPrimaryEmail ? 1 : 0) + (isCRMContactDetailsPageEnabled && (resolved?.contactId || resolved?.contactUuid) ? 1 : 0);
  const hasKebabItems = !hideKebabMenu && (isBlocked ? canUnblock && !hideUnblockFromMenu : canReschedule && !hideRescheduleFromMenu || canCancel && !hideRemoveFromMenu || canEditEmail || canBlock && !hideBlockFromMenu);
  const rescheduleHref = `/reschedulings/${invitee.uuid}?&return_to=${window.location.pathname}`;
  const rescheduleMenuLink = <BareItem as="a" href={rescheduleHref}>
      <ItemContents>
        <ArrowRoundRightIcon spacingRight={4} />
        {I18n.t('publisher.meetings.invitees.reschedule_invitee')}
      </ItemContents>
    </BareItem>;
  const kebab = <div className={inviteeActionsKebabHostCss}>
      <IconButton size="small" onClick={toggleGroupMenu} title={I18n.t('publisher.meetings.invitees.invitee_actions')} aria-label={I18n.t('publisher.meetings.invitees.invitee_actions')} aria-expanded={isExpanded}>
        <SettingsMenuVerticalIcon />
      </IconButton>
      <Dropdown>
        <StyledMenu>
          <div>
            {!isBlocked && canReschedule && !hideRescheduleFromMenu && <HostReschedule event={event} invitee={invitee} isInviteeOnlyReschedule sendAnalytics={sendAnalytics} Button={RescheduleKebabMenuButton}>
                {rescheduleMenuLink}
              </HostReschedule>}
            {!isBlocked && canEditEmail && <BareItem onClick={openEditEmailModal}>
                <ItemContents>
                  <EditPencilIcon spacingRight={4} />
                  {I18n.t('publisher.meetings.invitees.edit_invitee_email')}
                </ItemContents>
              </BareItem>}
            {!isBlocked && canCancel && !hideRemoveFromMenu && <BareItem onClick={openCancelInviteeModal}>
                <ItemContents>
                  <CloseCircleIcon spacingRight={4} />
                  {I18n.t('publisher.meetings.invitees.remove_invitee')}
                </ItemContents>
              </BareItem>}
            {canBlock && !hideBlockFromMenu && <BareItem onClick={handleBlockClick}>
                <ItemContents>
                  <BlockIcon spacingRight={4} />
                  {event.past ? I18n.t('publisher.block_invitee.action.block_attendee') : I18n.t('publisher.block_invitee.action.block_invitee')}
                </ItemContents>
              </BareItem>}
            {canUnblock && !hideUnblockFromMenu && <BareItem onClick={openUnblockModal} disabled={isOlderThan90Days}>
                <ItemContents>
                  <ArrowRoundLeftIcon spacingRight={4} />
                  {event.past ? I18n.t('publisher.block_invitee.action.unblock_attendee') : I18n.t('publisher.block_invitee.action.unblock_invitee')}
                </ItemContents>
              </BareItem>}
          </div>
        </StyledMenu>
      </Dropdown>
    </div>;
  return <>
      {showPrimaryActions && <PrimaryActionsRow data-testid="attendee-actions" className={primaryActionButtonCount === 3 ? primaryActionsRowThreeItemsCss : undefined}>
          {canMarkNoShow && !invitee.blockedByHost && (invitee.noShow ? <MinimalButton className={primaryInviteeActionButtonCss} onClick={handleUndoNoShow}>
                <ArrowRoundLeftIcon spacingRight={4} />
                {I18n.t('publisher.meetings.invitees.undo_no_show')}
              </MinimalButton> : <MinimalButton className={primaryInviteeActionButtonCss} onClick={handleNoShowClick}>
                <BlockIcon spacingRight={4} />
                {I18n.t('publisher.meetings.invitees.mark_no_show')}
              </MinimalButton>)}
          {hasContactForPrimaryEmail && <MinimalButton className={primaryInviteeActionButtonCss} onClick={handleEmailClick}>
              <MailSendEnvelopeIcon spacingRight={4} />
              {I18n.t('publisher.meetings.invitees.email')}
            </MinimalButton>}
          {isCRMContactDetailsPageEnabled && (resolved?.contactId || resolved?.contactUuid) && <MinimalButton className={primaryInviteeActionButtonCss} onClick={handleViewFullProfile}>
              <UserSingleIcon spacingRight={4} />
              {I18n.t('publisher.meetings.invitees.view_full_profile')}
            </MinimalButton>}
        </PrimaryActionsRow>}
      {hasKebabItems && kebab}
      {showPrimaryActions && isNoShowModalOpen && <NoShowModal inviteeName={invitee.name} onConfirm={handleMarkNoShowConfirm} onClose={closeNoShowModal} />}
      {showPrimaryActions && isEmailPopupOpen && emailInitialValues && <SendPopup initialFormValues={emailInitialValues} onSend={handleEmailSend} onCancel={closeEmailPopup} source={CRM_SHAREABLE} viewConfig={{
      isSubjectAtTop: true,
      isReminderFieldVisible: false,
      showConnectEmailContainer: true,
      showShareSubHeader: false
    }} />}
      {isCancelInviteeModalOpen && <CancelInviteeModal invitee={invitee} onClose={closeCancelInviteeModal} />}
      {isBlockModalOpen && <BlockModal event={event} invitee={invitee} onClose={closeBlockModal} />}
      {isUnblockModalOpen && <UnblockModal event={event} invitee={invitee} onClose={closeUnblockModal} />}
      {isBlockExpiredModalOpen && <BlockExpiredModal inviteeName={invitee.name} isPast={event.past} onClose={closeBlockExpiredModal} />}
    </>;
};

require("./invitee_actions.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./invitee_actions.js");